import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

// Button per the Figma design system ("Button / default", node 720:42824):
// primary = brand bg + skeuomorphic inner shadows + subtle white outer border.
const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-md font-semibold text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'border-2 border-white/10 bg-primary text-primary-foreground dark:border-white/20 shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),inset_0px_0px_0px_1px_rgba(16,24,40,0.18),inset_0px_-2px_0px_0px_rgba(16,24,40,0.05)] hover:bg-primary/90',
        destructive: 'bg-destructive text-white hover:bg-destructive/90',
        // "Button / success" — solid green, used for Unban account (Figma
        // 2167:26354). Green palette shared with the success Badge (#17b26a).
        success: 'bg-success text-white hover:bg-success-fg',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        // "Button / secondary" (Figma node 720:42830): white bg, neutral border,
        // secondary-fg text, skeuomorphic inner shadows — the modal Cancel action.
        secondary:
          'border border-input bg-background text-secondary-foreground shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),inset_0px_0px_0px_1px_rgba(16,24,40,0.18),inset_0px_-2px_0px_0px_rgba(16,24,40,0.05)] hover:bg-accent',
        // "Button / secondary-color" (Figma node 21:2088): white bg, brand-soft
        // border, brand-link text, skeuomorphic inner shadows.
        'brand-outline':
          'border border-brand-soft bg-background text-brand-link shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),inset_0px_0px_0px_1px_rgba(16,24,40,0.18),inset_0px_-2px_0px_0px_rgba(16,24,40,0.05)] hover:bg-accent',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
      },
      size: {
        default: 'h-10 px-3.5 py-2.5',
        sm: 'h-8 px-3',
        lg: 'h-11 px-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export function Button({ className, variant, size, type = 'button', ...props }: ButtonProps) {
  return (
    <button type={type} className={cn(buttonVariants({ variant, size, className }))} {...props} />
  );
}

export { buttonVariants };
